import React, { useState, useEffect, createContext, useContext } from 'react';
import { auth } from 'store/configStore';
import type firebase from 'firebase/compat/app';
import moment from 'moment';

import { clearSessionStorageCache } from 'helpers/indexedDBHelper';
import { apiURL } from 'config/baseURL';
import { gatewayFetch } from 'helpers/gateway';
import { clearSession, endSession, isSessionLoginPending, readSession, SESSION_STORAGE_KEY } from 'helpers/sessionAuth';
import { readErrorMessage } from 'helpers/apiError';
import Monitoring from '../utils/monitoring/Monitoring';

export const IDTOKEN = 'idtoken';
const IDTOKEN_TIMESTAMP = 'idtoken:timestamp';

const getSetToken = async (user: firebase.User) => {
	try {
		const token = await user.getIdToken(true);

		sessionStorage.setItem(IDTOKEN, token);
		sessionStorage.setItem(IDTOKEN_TIMESTAMP, new Date().toISOString());

		return token;
	} catch (error) {
		console.error('Error generating token:', error);
		Monitoring.logEvent('getSetToken', error);
		return null;
	}
};

/**
 * getIdToken get user id token then set token to localstorage
 * @param forceRefresh - Force fresh token from Firebase, bypassing cache
 */
export const getIdToken = async (forceRefresh = false) => {
	const user = auth().currentUser;
	if (!user) {
		return null;
	}

	// Force refresh bypasses cache (used for iframe token sync to avoid stale tokens)
	if (forceRefresh) {
		return await getSetToken(user);
	}

	const tokenLocal = sessionStorage.getItem(IDTOKEN);
	const tokenTimestamp = sessionStorage.getItem(IDTOKEN_TIMESTAMP);

	if (!tokenLocal || !tokenTimestamp) {
		return await getSetToken(user);
	}

	const diff = moment().diff(tokenTimestamp, 'minutes');

	// Refresh localstorage token if more than 45 mins
	if (Math.abs(diff) > 45) {
		return await getSetToken(user);
	}

	return tokenLocal;
};

const _signout = async () => auth().signOut();

const _signup = async ({ email, password }: { email: string; password: string }) =>
	auth()
		.createUserWithEmailAndPassword(email, password)
		.then((res) => res.user);

/** An Error carrying the same `code` the Firebase SDK put on its rejections. */
type AuthError = Error & { code: string; status?: number };

const authError = (code: string, message: string, status?: number): AuthError => {
	const error = new Error(message) as AuthError;
	error.code = code;
	if (status !== undefined) {
		error.status = status;
	}
	return error;
};

/**
 * api-users reports each failure as `{ message }` plus a status. The messages
 * are the exact strings its `confirmResetPassword` usecase throws, and they map
 * one-for-one onto the `auth/*` codes the SDK used to reject with, so no
 * distinction is lost by moving the call server-side.
 */
const CONFIRM_RESET_CODE_BY_MESSAGE: Record<string, string> = {
	'Reset Password Link has been expired': 'auth/expired-action-code',
	'Reset Password Link is invalid or has been used': 'auth/invalid-action-code',
	'Reset Password is not allowed': 'auth/operation-not-allowed',
	'User has been disabled by the administrator': 'auth/user-disabled',
	'Password does not meet the minimum requirements': 'auth/weak-password',
};

/**
 * Fallback for a message this build does not know - a reworded server string, a
 * Joi rejection, or a gateway error page that is not JSON at all. The status
 * still separates "the link is no good" (401) from "the request is no good"
 * (400) from "ours, not theirs" (5xx), so a caller branching on the code keeps
 * a usable answer instead of a single opaque failure.
 */
const confirmResetCodeForStatus = (status: number): string => {
	if (status === 401) {
		return 'auth/invalid-action-code';
	}
	if (status === 400) {
		return 'auth/argument-error';
	}
	return 'auth/internal-error';
};

/**
 * Confirm a password reset through api-users rather than in the browser.
 *
 * Firebase Auth migration Phase 1 mirrors every password into the credential
 * store, but only where a server can see the write. `auth().confirmPasswordReset`
 * runs entirely client-side, so a reset completed here left the store holding
 * the old password - a mismatch in Phase 2 shadow verification and a locked-out
 * user at the Phase 3 flip. `/users/confirm-password` performs the same Firebase
 * confirmation server-side and mirrors the result. It is gateway-whitelisted, so
 * it needs no session, which is what a reset link requires.
 *
 * The rejection contract is unchanged: callers still get an Error whose `code`
 * is the `auth/*` code the SDK would have raised for the same condition.
 */
const _confirmPasswordReset = async ({ code, password }: { code: string; password: string }) => {
	let response: Response;

	try {
		response = await gatewayFetch(`${apiURL}/users/confirm-password`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ newPassword: password, oobCode: code }),
		});
	} catch (error) {
		// Never reached the server. The SDK reported this as a network failure
		// and it is not the link or the password, so say so rather than
		// blaming either.
		throw authError('auth/network-request-failed', error instanceof Error ? error.message : String(error));
	}

	if (response.ok) {
		return;
	}

	// gatewayFetch resolves on a plain-fetch 4xx/5xx, so the body is the only
	// place the reason is recorded.
	const message = await readErrorMessage(response);

	throw authError(
		CONFIRM_RESET_CODE_BY_MESSAGE[message] || confirmResetCodeForStatus(response.status),
		message || `Confirm password reset failed with status ${response.status}`,
		response.status,
	);
};

const state = {
	user: null,
	isLoaded: false,

	signout: _signout,
	signup: _signup,

	confirmPasswordReset: _confirmPasswordReset,
} as ReturnType<typeof useProvider>;
const authContext = createContext(state);

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function ProvideAuth({ children }: { children: React.ReactNode | React.ReactNode[] }) {
	const auth = useProvider();

	return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useFirebaseAuth = () => {
	return useContext(authContext);
};

// Provider hook that creates auth object and handles state
function useProvider() {
	const [user, setUser] = useState<null | firebase.User>(null);
	const [isLoaded, setIsLoaded] = useState(false);

	// Subscribe to user on mount
	// Because this sets state in the callback it will cause any ...
	// ... component that utilizes this hook to re-render with the ...
	// ... latest auth object.
	useEffect(() => {
		// An active login can store its API session before Firebase's initial null callback.
		// Otherwise a confirmed signed-out state must also clear a leftover stored session.
		let signedIn = false;
		let signedInUserID: string | null = null;
		let signedInSession: ReturnType<typeof readSession> = null;
		const syncSameUserSession = (event: StorageEvent) => {
			if (event.key !== SESSION_STORAGE_KEY || !signedInUserID) {
				return;
			}
			const current = readSession();
			if (current?.userID === signedInUserID) {
				signedInSession = current;
			}
		};
		window.addEventListener('storage', syncSameUserSession);
		const unsubscribe = auth().onAuthStateChanged((user) => {
			if (user) {
				signedIn = true;
				signedInUserID = user.uid;
				setUser(user);

				// Clear any existing tokens before new user login
				// This prevents token contamination between users
				sessionStorage.removeItem(IDTOKEN);
				sessionStorage.removeItem(IDTOKEN_TIMESTAMP);

				// Clear any disabled user markers from previous session
				sessionStorage.removeItem('idtoken:disabled');
				sessionStorage.removeItem('idtoken:disabled:uid');
				sessionStorage.removeItem('idtoken:disabled:timestamp');

				// A stored API session belongs to exactly one account.
				const session = readSession();
				if (session && session.userID !== user.uid) {
					clearSession();
				}
				signedInSession = readSession();

				// NOTE: We don't generate tokens here anymore
				// Tokens will be generated on-demand by getIdToken() only after profile validation
			} else {
				// Firebase can deliver its initial null callback after a custom-token
				// login has already completed. Keep the live state until Firebase
				// itself reports that the user is gone.
				if (auth().currentUser || isSessionLoginPending()) {
					return;
				}
				setUser(null);

				// Every sign-out path (sidebar, header, freeze, client login) ends here.
				endSession(gatewayFetch, signedIn ? signedInSession : undefined);
				signedIn = false;
				signedInUserID = null;
				signedInSession = null;
				clearSessionStorageCache();
			}

			setIsLoaded(true);
		});

		// Cleanup subscription on unmount
		return () => {
			unsubscribe();
			window.removeEventListener('storage', syncSameUserSession);
			clearSessionStorageCache();
		};
	}, []);

	// Return the user object and auth methods
	return {
		user,
		isLoaded,
		signup: _signup,
		signout: _signout,
		confirmPasswordReset: _confirmPasswordReset,
	};
}
